import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
	ArrowUturnLeftIcon,
	CalendarDaysIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	InformationCircleIcon,
	LockClosedIcon,
	PaperAirplaneIcon,
	SunIcon,
	TrashIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog, Modal, Spinner } from '../../components/ui';
import { timeOffApi, ApiError, STAFF_TIME_OFF_REASONS } from '../../api';
import type {
	LocationClosure,
	StaffSalonHours,
	StaffSalonLocationHours,
	TimeOffPreview,
	TimeOffRecord,
	TimeOffType,
} from '../../types/timeOff';
import type { BusinessHoursDay, DayOfWeek } from '../../types/businessHours';
import { DAYS_OF_WEEK } from '../../types/businessHours';

// ─── Helpers ────────────────────────────────────────────────────

const DAY_LABELS: Record<DayOfWeek, string> = {
	MONDAY: 'Monday',
	TUESDAY: 'Tuesday',
	WEDNESDAY: 'Wednesday',
	THURSDAY: 'Thursday',
	FRIDAY: 'Friday',
	SATURDAY: 'Saturday',
	SUNDAY: 'Sunday',
};

/** "09:00" → "9:00 AM". The API may send HH:mm:ss. */
const to12h = (value?: string | null): string => {
	if (!value) return '';
	const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
	if (!match) return value;
	const hour = Number(match[1]);
	const suffix = hour >= 12 ? 'PM' : 'AM';
	const h12 = hour % 12 === 0 ? 12 : hour % 12;
	return `${h12}:${match[2]} ${suffix}`;
};

/** A day whose closing time is not after its opening time runs past midnight. */
const isOvernight = (day: BusinessHoursDay): boolean =>
	!day.closed && !day.open24 && Boolean(day.openTime) && day.closeTime <= day.openTime;

const todayIso = (): string => {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
		now.getDate()
	).padStart(2, '0')}`;
};

const formatIsoDate = (iso: string): string => {
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const formatCurrency = (amount: number | null | undefined): string =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0);

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const hoursToDays = (hours: number) => Math.round(hours / 24);

/** "5 hours" under a day, "2 days" from then on — matches the server's wording. */
const describeHours = (hours: number): string => {
	if (hours < 24) return plural(hours, 'hour');
	return plural(Math.floor(hours / 24), 'day');
};

const STATUS_STYLES: Record<string, string> = {
	BLOCKED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
	APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
	PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
	DECLINED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
	COMPLETED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const statusStyle = (record: TimeOffRecord) =>
	STATUS_STYLES[record.past && record.active ? 'COMPLETED' : record.status] ??
	STATUS_STYLES.COMPLETED;

// ─── Salon hours (read-only) ────────────────────────────────────

const SalonHoursCard = ({ location }: { location: StaffSalonLocationHours }) => {
	const week = useMemo(
		() =>
			DAYS_OF_WEEK.map(
				dow =>
					location.hours.find(h => h.dayOfWeek?.toUpperCase() === dow) ?? {
						dayOfWeek: dow,
						closed: true,
						open24: false,
						openTime: '',
						closeTime: '',
					}
			),
		[location.hours]
	);

	return (
		<div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
			{week.map(day => (
				<div
					key={day.dayOfWeek}
					className={`grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr] items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
						day.closed ? 'bg-gray-50 dark:bg-gray-800/60' : 'bg-white dark:bg-gray-800'
					}`}
				>
					<div className="text-[13px] font-bold text-gray-900 dark:text-white">
						{DAY_LABELS[day.dayOfWeek]}
					</div>
					<div className="flex items-center gap-2 flex-wrap">
						{day.closed ? (
							<span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
								Closed
							</span>
						) : day.open24 ? (
							<>
								<span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
									Open 24 hours
								</span>
								<span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 px-2.5 py-0.5 rounded-full">
									24 hrs
								</span>
							</>
						) : (
							<>
								<span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
									{to12h(day.openTime)} – {to12h(day.closeTime)}
								</span>
								{isOvernight(day) && (
									<span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-full">
										Closes next day
									</span>
								)}
							</>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

const ClosuresList = ({ closures }: { closures: LocationClosure[] }) => {
	if (closures.length === 0) {
		return (
			<p className="text-sm text-gray-500 dark:text-gray-400 italic">
				No salon closures scheduled.
			</p>
		);
	}
	return (
		<ul className="space-y-2">
			{closures.map(closure => (
				<li
					key={closure.id}
					className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
				>
					<div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 flex items-center justify-center flex-shrink-0">
						<LockClosedIcon className="w-5 h-5" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-bold text-gray-900 dark:text-white truncate">
							{closure.name}
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							{closure.dateLabel} · {plural(closure.dayCount, 'day')}
						</p>
					</div>
					<span className="text-[11px] font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 px-2.5 py-0.5 rounded-full whitespace-nowrap">
						Salon closed
					</span>
				</li>
			))}
		</ul>
	);
};

// ─── Take Time Off modal ────────────────────────────────────────

interface TimeOffModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSaved: (record: TimeOffRecord) => void;
	locations: StaffSalonLocationHours[];
}

const TimeOffModal = ({ isOpen, onClose, onSaved, locations }: TimeOffModalProps) => {
	const today = todayIso();
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [type, setType] = useState<TimeOffType>('VACATION');
	const [note, setNote] = useState('');
	const [locationId, setLocationId] = useState<number | ''>('');
	const [preview, setPreview] = useState<TimeOffPreview | null>(null);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		setStartDate('');
		setEndDate('');
		setType('VACATION');
		setNote('');
		setLocationId('');
		setPreview(null);
		setPreviewError(null);
	}, [isOpen]);

	// The server evaluates every date in the range against the notice rule and the
	// staff member's bookings, so this re-runs whenever either date changes. The
	// submission runs the same evaluation again — the preview is advice, not a lock.
	useEffect(() => {
		if (!isOpen || !startDate || !endDate) {
			setPreview(null);
			return;
		}
		if (endDate < startDate) {
			setPreview(null);
			setPreviewError('The last day off cannot be before the first.');
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			setIsPreviewing(true);
			setPreviewError(null);
			try {
				const data = await timeOffApi.previewMine({
					startDate,
					endDate,
					type,
					note: note.trim() || undefined,
					locationId: locationId === '' ? undefined : locationId,
				});
				if (!cancelled) setPreview(data);
			} catch (err) {
				if (cancelled) return;
				setPreview(null);
				setPreviewError((err as ApiError).message || 'Could not check these dates');
			} finally {
				if (!cancelled) setIsPreviewing(false);
			}
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [isOpen, startDate, endDate, type, locationId, note]);

	const handleStartChange = (value: string) => {
		setStartDate(value);
		if (!endDate || endDate < value) setEndDate(value);
	};

	const canSubmit = Boolean(preview?.canSubmit) && !isPreviewing && !isSubmitting;
	const requiresApproval = preview?.requiresApproval ?? false;

	const handleSubmit = async () => {
		if (!canSubmit || !preview) return;
		setIsSubmitting(true);
		try {
			const record = await timeOffApi.createMine({
				startDate,
				endDate,
				type,
				note: note.trim() || undefined,
				locationId: locationId === '' ? undefined : locationId,
			});
			onSaved(record);
			onClose();
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not save your time off');
		} finally {
			setIsSubmitting(false);
		}
	};

	const inputClass =
		'w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Take Time Off" size="lg">
			<div className="space-y-4">
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							First Day Off
						</label>
						<input
							type="date"
							min={today}
							value={startDate}
							onChange={e => handleStartChange(e.target.value)}
							className={inputClass}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Last Day Off
						</label>
						<input
							type="date"
							min={startDate || today}
							value={endDate}
							onChange={e => setEndDate(e.target.value)}
							className={inputClass}
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Reason
						</label>
						<select
							value={type}
							onChange={e => setType(e.target.value as TimeOffType)}
							className={inputClass}
						>
							{STAFF_TIME_OFF_REASONS.map(reason => (
								<option key={reason.value} value={reason.value}>
									{reason.label}
								</option>
							))}
						</select>
					</div>
					{locations.length > 1 && (
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
								Away from
							</label>
							<select
								value={locationId}
								onChange={e =>
									setLocationId(
										e.target.value === '' ? '' : Number(e.target.value)
									)
								}
								className={inputClass}
							>
								<option value="">All my locations</option>
								{locations.map(loc => (
									<option key={loc.locationId} value={loc.locationId}>
										{loc.locationName}
									</option>
								))}
							</select>
						</div>
					)}
				</div>

				{requiresApproval && (
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Note for your provider
						</label>
						<input
							type="text"
							value={note}
							onChange={e => setNote(e.target.value)}
							maxLength={500}
							placeholder="Why you need these dates (helps them decide)"
							className={inputClass}
						/>
					</div>
				)}

				{/* Checks */}
				{previewError && (
					<div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
						{previewError}
					</div>
				)}

				{isPreviewing && !preview && (
					<div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
						<Spinner size="sm" /> Checking these dates…
					</div>
				)}

				{preview && <PreviewPanels preview={preview} />}
			</div>

			<div className="flex justify-end gap-3 mt-6">
				<Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					type="button"
					variant="primary"
					onClick={handleSubmit}
					disabled={!canSubmit}
					isLoading={isSubmitting}
				>
					{preview?.submitLabel ?? 'Block These Dates'}
				</Button>
			</div>
		</Modal>
	);
};

const CheckBox = ({
	tone,
	title,
	children,
}: {
	tone: 'ok' | 'warn' | 'info' | 'request';
	title: string;
	children?: ReactNode;
}) => {
	const tones = {
		ok: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-100',
		warn: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100',
		info: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-100',
		request:
			'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-900 dark:text-indigo-100',
	};
	return (
		<div className={`rounded-xl border px-4 py-3 ${tones[tone]}`}>
			<p className="text-sm font-bold">{title}</p>
			{children && <div className="mt-1 text-[13px] leading-relaxed">{children}</div>}
		</div>
	);
};

const PreviewPanels = ({ preview }: { preview: TimeOffPreview }) => {
	const weekendInRange = preview.days.some(d => d.weekend && !d.salonClosed);
	const rule = weekendInRange
		? `Weekdays need ${hoursToDays(preview.weekdayNoticeHours)} days' notice, weekends need ${hoursToDays(preview.weekendNoticeHours)}.`
		: `Weekdays need ${hoursToDays(preview.weekdayNoticeHours)} days' notice.`;
	const closedDays = preview.days.filter(d => d.salonClosed);

	if (preview.outcome === 'INVALID') {
		return (
			<CheckBox tone="warn" title={preview.blockingMessage ?? 'These dates cannot be used'} />
		);
	}

	if (preview.outcome === 'NOTHING_TO_BLOCK') {
		return (
			<CheckBox tone="info" title="✓ Salon already closed">
				The salon isn’t open on these dates, so there is nothing to block.
				{closedDays.length > 0 && (
					<ul className="mt-2 space-y-0.5">
						{closedDays.map(d => (
							<li key={d.date}>
								{d.dayName} {formatIsoDate(d.date)} — {d.closedReason}
							</li>
						))}
					</ul>
				)}
			</CheckBox>
		);
	}

	return (
		<div className="space-y-3">
			{/* Notice */}
			{preview.noticeShortfalls.length === 0 ? (
				<CheckBox tone="ok" title="✓ Enough notice">
					{rule} Every open day in this range is clear.
				</CheckBox>
			) : (
				<CheckBox
					tone="warn"
					title={`⚠ Short notice on ${plural(preview.noticeShortfalls.length, 'day')}`}
				>
					{rule} Your provider will need to approve it.
					<ul className="mt-2 space-y-1.5">
						{preview.noticeShortfalls.map(d => (
							<li
								key={d.date}
								className="rounded-lg bg-white/70 dark:bg-gray-800/40 px-3 py-2"
							>
								<div className="flex flex-wrap items-center gap-2 font-semibold">
									{d.dayName} {formatIsoDate(d.date)}
									<span
										className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
											d.weekend
												? 'bg-amber-200/70 text-amber-900 dark:bg-amber-800/60 dark:text-amber-100'
												: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200'
										}`}
									>
										{d.weekend ? 'Weekend' : 'Weekday'}
									</span>
								</div>
								<div className="text-xs mt-0.5">
									Needs {hoursToDays(d.requiredNoticeHours)} days’ notice — you’re
									giving {describeHours(d.providedNoticeHours)}
								</div>
							</li>
						))}
					</ul>
				</CheckBox>
			)}

			{/* Closed days inside the range */}
			{closedDays.length > 0 && (
				<CheckBox tone="info" title={`${plural(closedDays.length, 'day')} already closed`}>
					The salon is shut on these dates, so they are skipped — only the{' '}
					{plural(preview.openDayCount, 'open day')} will be blocked.
					<ul className="mt-1.5 space-y-0.5">
						{closedDays.map(d => (
							<li key={d.date}>
								{d.dayName} {formatIsoDate(d.date)} — {d.closedReason}
							</li>
						))}
					</ul>
				</CheckBox>
			)}

			{/* Bookings */}
			{preview.affectedBookingCount === 0 ? (
				<CheckBox tone="ok" title="✓ No bookings affected">
					You have nothing booked on these dates.
				</CheckBox>
			) : (
				<CheckBox
					tone="warn"
					title={`⚠ ${plural(preview.affectedBookingCount, 'booking')} in this range`}
				>
					Your provider decides what happens to these — you can’t cancel or move a
					customer’s appointment yourself.{' '}
					<strong>{formatCurrency(preview.affectedRevenue)}</strong> of booked work is
					affected.
					<ul className="mt-2 space-y-1.5">
						{preview.affectedBookings.map(b => (
							<li
								key={b.appointmentId}
								className="rounded-lg bg-white/70 dark:bg-gray-800/40 px-3 py-2"
							>
								<div className="font-semibold">{b.customerName ?? 'Customer'}</div>
								<div className="text-xs mt-0.5">
									{b.whenLabel} · {b.serviceName ?? 'Service'} ·{' '}
									{formatCurrency(b.amount)}
								</div>
							</li>
						))}
					</ul>
				</CheckBox>
			)}

			{/* Outcome */}
			{preview.requiresApproval ? (
				<CheckBox tone="request" title="📨 This goes to your Provider">
					Your Provider has to approve it because:
					<ul className="mt-1 list-disc pl-5 space-y-0.5">
						{preview.reasons.map(reason => (
							<li key={reason}>{reason}</li>
						))}
					</ul>
					<p className="mt-1.5">Your calendar stays open until they approve.</p>
				</CheckBox>
			) : (
				<CheckBox tone="ok" title="✓ You can block this yourself">
					These dates go straight onto your calendar. No approval needed — customers won’t
					be able to book you.
				</CheckBox>
			)}
		</div>
	);
};

// ─── Time-off list ──────────────────────────────────────────────

const TimeOffItem = ({
	record,
	onWithdraw,
	onRemove,
}: {
	record: TimeOffRecord;
	onWithdraw: (record: TimeOffRecord) => void;
	onRemove: (record: TimeOffRecord) => void;
}) => {
	const isPending = record.status === 'PENDING';
	const isDeclined = record.status === 'DECLINED';
	const meta = isPending
		? 'Sent to your Provider'
		: isDeclined
			? record.decisionNote
				? `Declined — ${record.decisionNote}`
				: 'Declined — your calendar stays open'
			: record.past
				? 'Completed'
				: record.requestedByStaff
					? 'Customers can’t book you'
					: 'Entered by your Provider — customers can’t book you';

	return (
		<li
			className={`flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3.5 ${
				record.past ? 'opacity-70' : ''
			}`}
		>
			<div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center flex-shrink-0">
				<SunIcon className="w-5 h-5" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-bold text-gray-900 dark:text-white">
					{record.typeLabel}
				</p>
				<p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
					{record.dateLabel} · {plural(record.dayCount, 'day')}
					{!record.fullDay && record.timeLabel ? ` · ${record.timeLabel}` : ''}
					{record.locationId ? ` · ${record.locationName}` : ''}
				</p>
				<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{meta}</p>
				{isPending && record.escalationReasons.length > 0 && (
					<p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
						Needs approval because: {record.escalationReasons.join('; ')}
					</p>
				)}
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<span
					className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusStyle(record)}`}
				>
					{record.statusLabel}
				</span>
				{record.canWithdraw && (
					<button
						type="button"
						onClick={() => onWithdraw(record)}
						title="Withdraw request"
						aria-label="Withdraw request"
						className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 cursor-pointer"
					>
						<ArrowUturnLeftIcon className="w-4 h-4" />
					</button>
				)}
				{record.canRemove && (
					<button
						type="button"
						onClick={() => onRemove(record)}
						title="Remove time off"
						aria-label="Remove time off"
						className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer"
					>
						<TrashIcon className="w-4 h-4" />
					</button>
				)}
			</div>
		</li>
	);
};

// ─── Page ───────────────────────────────────────────────────────

const HoursTimeOff = () => {
	const [salon, setSalon] = useState<StaffSalonHours | null>(null);
	const [records, setRecords] = useState<TimeOffRecord[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<{
		kind: 'withdraw' | 'remove';
		record: TimeOffRecord;
	} | null>(null);
	const [isActing, setIsActing] = useState(false);

	const load = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const [hours, mine] = await Promise.all([
				timeOffApi.mySalonHours(),
				timeOffApi.listMine(),
			]);
			setSalon(hours);
			setRecords(mine);
			setActiveLocationId(prev => prev ?? hours.locations[0]?.locationId ?? null);
		} catch (err) {
			setError((err as ApiError).message || 'Failed to load your hours and time off');
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const activeLocation = useMemo(
		() =>
			salon?.locations.find(l => l.locationId === activeLocationId) ??
			salon?.locations[0] ??
			null,
		[salon, activeLocationId]
	);

	const pendingCount = useMemo(
		() => records.filter(r => r.status === 'PENDING' && !r.past).length,
		[records]
	);
	const weekdayDays = hoursToDays(salon?.weekdayNoticeHours ?? 72);
	const weekendDays = hoursToDays(salon?.weekendNoticeHours ?? 168);

	const upsert = (record: TimeOffRecord) =>
		setRecords(prev => {
			const rest = prev.filter(r => r.id !== record.id);
			const keep = record.status !== 'WITHDRAWN' && record.status !== 'REMOVED';
			return (keep ? [...rest, record] : rest).sort((a, b) =>
				a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0
			);
		});

	const handleSaved = (record: TimeOffRecord) => {
		upsert(record);
		if (record.status === 'PENDING') {
			toast(`Request sent to your Provider for ${record.dateLabel}`, { icon: '📨' });
		} else {
			toast.success(`Blocked ${record.dateLabel} — customers can’t book you`);
		}
	};

	const confirmAction = async () => {
		if (!pendingAction) return;
		setIsActing(true);
		try {
			const updated =
				pendingAction.kind === 'withdraw'
					? await timeOffApi.withdrawMine(pendingAction.record.id)
					: await timeOffApi.removeMine(pendingAction.record.id);
			upsert(updated);
			toast.success(
				pendingAction.kind === 'withdraw'
					? 'Request withdrawn — your Provider has been told'
					: 'Time off removed — those dates are bookable again'
			);
			setPendingAction(null);
		} catch (err) {
			toast.error((err as ApiError).message || 'That didn’t work. Please try again.');
		} finally {
			setIsActing(false);
		}
	};

	if (isLoading) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			</AppLayout>
		);
	}

	if (error) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<Button variant="primary" size="sm" onClick={load}>
						Retry
					</Button>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex items-center gap-4 mb-6">
				<div
					className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
					style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
				>
					<ClockIcon className="w-6 h-6 text-white" />
				</div>
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
						Hours &amp; Time Off
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
						Your salon’s opening hours and your own time off
					</p>
				</div>
			</div>

			{/* Summary banner */}
			<div
				className={`flex items-center gap-2.5 rounded-xl border px-4 py-3 mb-5 ${
					pendingCount > 0
						? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
						: 'border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20'
				}`}
			>
				<InformationCircleIcon
					className={`w-5 h-5 flex-shrink-0 ${
						pendingCount > 0
							? 'text-amber-600 dark:text-amber-300'
							: 'text-violet-600 dark:text-violet-300'
					}`}
				/>
				<span
					className={`text-[13px] font-medium ${
						pendingCount > 0
							? 'text-amber-900 dark:text-amber-200'
							: 'text-violet-900 dark:text-violet-200'
					}`}
				>
					{pendingCount > 0 ? (
						<>
							You have <strong>{plural(pendingCount, 'request')}</strong> waiting on
							your Provider. Everything else is blocked already.
						</>
					) : (
						<>
							Block your own dates any time. Approval is only needed inside{' '}
							<strong>{weekdayDays} days</strong> for weekdays,{' '}
							<strong>{weekendDays} days</strong> for weekends, or if you already have
							a booking.
						</>
					)}
				</span>
			</div>

			{/* Salon hours */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-5">
				<div className="flex flex-wrap items-start justify-between gap-3 mb-4">
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Salon Hours
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
							Set by your provider — these are the hours you can be booked
						</p>
					</div>
					{salon && salon.locations.length > 1 && (
						<div className="flex flex-wrap gap-2">
							{salon.locations.map(loc => (
								<button
									key={loc.locationId}
									type="button"
									onClick={() => setActiveLocationId(loc.locationId)}
									className={`px-3 py-1.5 rounded-full text-xs font-semibold border cursor-pointer transition-colors ${
										activeLocation?.locationId === loc.locationId
											? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
											: 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
									}`}
								>
									{loc.locationName}
								</button>
							))}
						</div>
					)}
				</div>
				{activeLocation ? (
					<>
						<SalonHoursCard location={activeLocation} />
						<p className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
							<LockClosedIcon className="w-3.5 h-3.5" /> Only the salon owner can
							change these hours.
						</p>
					</>
				) : (
					<p className="text-sm text-gray-500 dark:text-gray-400 italic">
						You are not assigned to a location yet.
					</p>
				)}
			</div>

			{/* Closures */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-5">
				<div className="mb-4">
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
						Salon Closures
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						Days the whole salon is closed — you don’t need to request these
					</p>
				</div>
				<ClosuresList closures={activeLocation?.closures ?? []} />
			</div>

			{/* My time off */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700">
				<div className="flex flex-wrap items-start justify-between gap-3 mb-4">
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							My Time Off
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
							Block your calendar so customers can’t book you. Weekdays need{' '}
							{weekdayDays} days’ notice, weekends need {weekendDays}.
						</p>
					</div>
					<Button variant="primary" size="sm" onClick={() => setIsModalOpen(true)}>
						<SunIcon className="w-4 h-4" /> Take Time Off
					</Button>
				</div>

				{records.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-10 text-center">
						<CalendarDaysIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
						<p className="text-sm text-gray-500 dark:text-gray-400">
							No time off booked. You’re available during all salon hours.
						</p>
					</div>
				) : (
					<ul className="space-y-2">
						{records.map(record => (
							<TimeOffItem
								key={record.id}
								record={record}
								onWithdraw={r => setPendingAction({ kind: 'withdraw', record: r })}
								onRemove={r => setPendingAction({ kind: 'remove', record: r })}
							/>
						))}
					</ul>
				)}
			</div>

			<TimeOffModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onSaved={handleSaved}
				locations={salon?.locations ?? []}
			/>

			<ConfirmDialog
				isOpen={pendingAction !== null}
				onClose={() => (isActing ? undefined : setPendingAction(null))}
				onConfirm={confirmAction}
				isLoading={isActing}
				variant={pendingAction?.kind === 'withdraw' ? 'primary' : 'danger'}
				title={
					pendingAction?.kind === 'withdraw'
						? 'Withdraw this request?'
						: 'Remove this time off?'
				}
				confirmLabel={
					pendingAction?.kind === 'withdraw' ? 'Withdraw Request' : 'Remove Time Off'
				}
				icon={
					pendingAction?.kind === 'withdraw' ? (
						<PaperAirplaneIcon className="w-6 h-6" />
					) : (
						<ExclamationTriangleIcon className="w-6 h-6" />
					)
				}
				message={
					pendingAction?.kind === 'withdraw'
						? `Your Provider will no longer review ${pendingAction.record.dateLabel}. Your calendar stays available.`
						: pendingAction
							? `${pendingAction.record.dateLabel} will be bookable again${
									pendingAction.record.status === 'APPROVED'
										? ', and your Provider will be told'
										: ''
								}.`
							: ''
				}
			/>
		</AppLayout>
	);
};

export default HoursTimeOff;
